<template>
<div v-if="modal.isVisible" class="modal-overlay">
      <div 
        class="modal-content"
        :style="{
          // Dynamic border color based on type
          'border-top': '4px solid ' + (
            modal.type === 'success' ? '#10b981' :         // green-500
            (modal.type === 'error' || modal.type === 'failure' ? '#ef4444' : '#3b82f6') // red-500 or blue-500
          )
        }"
      >
        <h3 class="modal-title" 
            :style="{
              // Dynamic text color based on type
              'color': (modal.type === 'success' ? '#059669' : (modal.type === 'error' || modal.type === 'failure' ? '#dc2626' : '#1d4ed8')) 
            }">
          {{ modal.title }}
        </h3>
        <p class="modal-message">{{ modal.message }}</p>
        <div class="modal-actions">
          <button @click="hideModal" class="modal-button">
            Close
          </button>
        </div>
      </div>
    </div>

  <div class="appointment-page-wrapper">
    <NavigationBarTop />
    <div class="appointment-container">

      <div v-if="isLoading" class="loading-state">
        <div class="loader"></div>
        <p class="loading-text">Fetching your appointment...</p>
      </div>

      <div v-else-if="error" class="error-box">
         <h2 class="error-title">Oops!</h2>
         <p class="error-message">{{ error }}</p>
      </div>

      <div v-else-if="appointment" class="appointment-details-card">
        <h1 class="card-title">Manage Appointment</h1>
        <p class="card-subtitle">View or modify your upcoming visit.</p>

        <div class="detail-section">
          <h3 class="section-heading">Current Booking</h3>
          <div class="detail-item">
            <span class="detail-label">Service:</span>
            <span class="detail-value service-value">{{ appointment.service_name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Stylist:</span>
            <span class="detail-value">{{ appointment.stylist_name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Date:</span>
            <span class="detail-value">{{ formatDate(appointment.start_time) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Time:</span>
            <span class="detail-value">{{ formatTime(appointment.start_time) }} - {{ formatTime(appointment.end_time) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Status:</span>
            <span class="detail-value" :class="statusClass">{{ appointment.appointment_status?.toUpperCase() }}</span>
          </div>
        </div>

        <div v-if="newlySelectedService || newBookingDetails" class="preview-selection-area">
            <h3 class="preview-title">Proposed Changes</h3>
            <div class="preview-grid">
                <div>
                    <span class="preview-label">New Service</span>
                    <span class="preview-value">{{ newlySelectedService?.service_name || appointment.service_name }}</span>
                </div>
                <div>
                    <span class="preview-label">New Stylist</span>
                    <span class="preview-value">{{ newBookingDetails?.stylist_name || 'Keep Current' }}</span>
                </div>
                <div>
                    <span class="preview-label">New Date</span>
                    <span class="preview-value">{{ newBookingDetails ? formatDate(newBookingDetails.date) : 'No change' }}</span>
                </div>
                <div>
                    <span class="preview-label">New Time</span>
                    <span class="preview-value">{{ newBookingDetails ? newBookingDetails.startTime : 'No change' }}</span>
                </div>
            </div>
            <button 
                @click="submitModification" 
                :disabled="isSubmitting" 
                class="btn-confirm-save"
            >
                {{ isSubmitting ? 'Saving...' : 'Confirm & Save Changes' }}
            </button>
        </div>

        <div class="action-row" v-if="!showServices && !bookingDetails">
            <button @click="updateAppointment" class="btn-update">Change Service or Time</button>
            <button @click="cancelAppointment" class="btn-cancel">Cancel Appointment</button>
        </div>

        <div class="servicesAndSlots">
            <AllServiceComponent 
                v-if="showServices" 
                @select-service="handleServiceSelection" 
            />
            
            <SelectDnTService 
                v-else-if="bookingDetails" 
                :service-data="newlySelectedService || { id: appointment.service_id }" 
                @continue-to-form="handleSaveChanges" 
                @select-another-service="handleBackToASC"
            />
        </div>
      </div> 
    </div>
  </div>
</template>

<script>
// The appointment is suppose to give the user the opportunity to
// change the service and the slot. So we would import the 
// service and the slot components, and the changes would be 
// sent to the backend
import axios from 'axios';
import NavigationBarTop from '@/components/NavigationBarTop.vue';

import AllServiceComponent from '@/components/AllServiceComponent.vue'
import SelectDnTService from '@/components/SelectDnTService.vue' 


// Mock API path structure
const APPOINTMENT_API_BASE = `${process.env.VUE_APP_API_URL}/appointment`;

export default {
    name: 'AppointmentPage',
    components: {
        NavigationBarTop,
        AllServiceComponent,
        SelectDnTService,
    },
    data() {
        return {
            appointment: null, //this is the actual line, the next lines are for testing
            
            isLoading: true, // it is suppose to be true
            isSubmitting: false,
            error: null,
            submitMessage: null,
            componentIs: "appointment",
            form: {
              
                service_id: null, 
                slot_id: null,
                start_time: '',
            },
            modal: {
                isVisible: false,
                title: '',
                message: '',
                type: 'info' // success, error, info, failure
            },

            // Manage the loading of the appointment sectino
            showServices: false,
            bookingDetails: false,

            // Manage updates
            newlySelectedService: null,
            newBookingDetails: null,
        };
    },
    computed: {
        formattedSlot() {
            if (!this.appointment || !this.appointment.slot) return 'N/A';
            try {
                // Basic formatting assumption, might need timezone handling
                const date = new Date(this.appointment.slot);
                return date.toLocaleString();
            } catch (e) {
                return this.appointment.slot;
            }
        },
        statusClass() {
            if (!this.appointment) return '';
            switch (this.appointment.appointment_status.toLowerCase()) {
                case 'completed':
                    return 'text-green-600 font-bold';
                case 'booked':
                    return 'text-yellow-600 font-bold';
                case 'cancelled':
                    return 'text-red-600 font-bold';
                default:
                    return 'text-gray-600';
            }
        },
        submitStatusClass() {
            return this.submitMessage && this.submitMessage.includes('successful') 
                ? 'text-green-600 bg-green-50 p-3 rounded' 
                : 'text-red-600 bg-red-50 p-3 rounded';
        }
    },
    async mounted() {
        console.log("I have mounted appointment!!")
        const id = this.$route.params.appointmentId;
        const token = this.$route.query.token;
        // this.fetchAppointmentDetails();

        if (id && token) {
    await this.fetchAppointmentDetails(id, token);
    this.isLoading=false
  } else {
    this.error = "Invalid management link.";
    this.loading = false;
  }
    },
    methods: {
         // Modal Handlers
        showModal(title, message, type = 'info') {
            this.modal = { isVisible: true, title, message, type };
        },
        hideModal() {
            this.modal.isVisible = false;
            // Optionally clear data after closing modal
            this.modal.title = '';
            this.modal.message = '';
        },
        async fetchAppointmentDetails(id, token) {
            this.isLoading = true;
            /*
            try {
                const appointmentId = this.$route.params.appointmentId;
                const token = this.$route.query.token;
                const url = `${APPOINTMENT_API_BASE}/${appointmentId}/manage?token=${token}`;
                
                const response = await axios.get(url);
                this.appointment = response.data;

            } catch (err) {
                console.error("Fetch Appointment Error:", err);
                if (err.response && err.response.status === 403) {
                    this.error = "This management link has expired for your security. Please request a new link.";
                } else {
                    this.error = "We couldn't load your appointment. Please check your internet connection.";
                }
            } finally {
                this.isLoading = false;
            }*/
           try {
      // Note: check if your VUE_APP_API_URL includes /api
      // The final URL should be: https://api.kcafrobraids.com/api/appointment/2/manage?token=...
      console.log("before accessing the db")
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/appointment/${id}/manage`, {
        params: { token: token }
      });
      this.appointment = response.data;
      console.log(this.appointment)
      //this.loading = false
    } catch (err) {
      this.error = err.response?.data?.error || "Could not load appointment.";
    } finally {
      this.isLoading = false;
    }
    this.isLoading = false
  
        },

        updateAppointment() {
            this.showServices = true
        },
        /*handleServiceSelection(service) {
            this.showServices = false
            this.bookingDetails = true
            this.newlySelectedService = service
        },*/

        handleSaveChanges(details) {
            this.newBookingDetails = details
            this.bookingDetails = false; // Hide the date picker after selection
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        formatNewSlot(date, time) {
        if (!date || !time) return 'Pending...';
        // Returns a readable string like "Dec 20, 2025 at 10:00 AM"
        return `${new Date(date).toLocaleDateString()} at ${time}`;
        },

        async cancelAppointment() {
            if (!confirm("Are you sure you want to cancel this appointment? This action cannot be undone.")) return;
            
            this.isSubmitting = true;
            this.submitMessage = null;

            try {
                const appointmentId = this.appointment.appointment_id;
                const token = this.$route.query.token; // Retrieve token from URL

                // We pass the token as a query parameter for verification
                const url = `${APPOINTMENT_API_BASE}/cancel-payment/${appointmentId}?token=${token}`;
                
                const response = await axios.delete(url); 

                this.appointment.appointment_status = 'cancelled';
                this.showModal('Cancelled', 'Your appointment has been successfully cancelled.', 'success');

            } catch (err) {
                console.error("Cancellation Error:", err);
                const errorMsg = err.response?.data?.error || 'An error occurred during cancellation.';
                this.showModal('Error', errorMsg, 'error');
            } finally {
                this.isSubmitting = false;
            }
        },

        formatDate(dateStr) {
        if (!dateStr) return '...';
        return new Date(dateStr).toLocaleDateString('en-US', { 
            weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' 
        });
    },
    formatTime(dateStr) {
        if (!dateStr) return '...';
        return new Date(dateStr).toLocaleTimeString('en-US', { 
            hour: '2-digit', minute: '2-digit' 
        });
    },

    // --- Flow Management ---
    updateAppointment() {
        this.showServices = true;
        this.newlySelectedService = null;
        this.newBookingDetails = null;
    },

    handleServiceSelection(service) {
        this.newlySelectedService = service;
        this.showServices = false;
        this.bookingDetails = true; // Move to Date & Time selection
    },
    handleBackToASC(){
        console.log("Switching back to choosew a service")
        this.showServices = true;
        this.bookingDetails = false;
        this.newlySelectedService = null;
    },

    handleSaveChanges(details) {
        // details typically contains { slot_id, date, startTime, stylist_name }
        this.newBookingDetails = details;
        this.bookingDetails = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // --- API Calls ---
    async submitModification() {
        if (!this.newBookingDetails) {
            alert("Please select a new date and time.");
            return;
        }

        this.isSubmitting = true;
        try {
            const appointmentId = this.appointment.appointment_id;
            const token = this.$route.query.token;

            const payload = {
                service_id: this.newlySelectedService?.id || this.appointment.service_id,
                slot_id: this.newBookingDetails.slot_id,
            };

            const url = `${APPOINTMENT_API_BASE}/update-payment/${appointmentId}`;
            const response = await axios.put(url, payload);

            this.appointment.service_name = this.newlySelectedService?.service_name || this.appointment.service_name;
            this.appointment.stylist_name = this.newBookingDetails.stylist_name;
            
            await this.fetchAppointmentDetails();

            // Clear the "Draft" state
            this.newlySelectedService = null;
            this.newBookingDetails = null;
            
            alert("Appointment updated successfully!");

        } catch (err) {
            console.error("Update Error:", err);
            alert(err.response?.data?.error || "Failed to update appointment.");
        } finally {
            this.isSubmitting = false;
        }
    },
    }
}
</script>

<style scoped>


/* Base Layout */
.appointment-page-wrapper {
    min-height: 100vh;
    background-color: #F9F9F9;
}

.appointment-container {
    max-width: 800px;
    margin: 40px auto;
    padding: 0 20px;
    font-family: 'Georgia', 'Times New Roman', serif;
    color: #1F2937; /* Almost Black text for high contrast */
}

.appointment-details-card {
    background-color: #F9F9F9;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.card-title {
    font-size: 2.25rem; 
    font-weight: 900;
    color: #5d0e1f;
    margin-bottom: 5px;
    letter-spacing: 1px;
}

.card-subtitle {
    color: #4B5563; /* Gray 600 */
    margin-bottom: 24px;
}

/* --- Details Section --- */
.detail-section {
    margin-top: 20px;
    padding: 15px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    background-color: #F3F4F6; /* Light gray for current details */
}

.section-heading {
    font-size: 1.25rem;
    font-weight: 700;
    color: #A87900;
    margin-bottom: 15px;
}

.detail-item {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px dashed #D1D5DB;
}

.detail-item:last-child {
    border-bottom: none;
}

.detail-label {
    font-weight: 500;
    color: #4B5563; 
}

.detail-value {
    font-weight: 600;
    color: #1F2937; 
}

.service-value {
    color: #4338CA; /* Indigo 700 */
    font-weight: 600;
}

/* Status Classes (used by computed property) */
.status-confirmed {
    color: var(--color-status-confirmed);
    font-weight: bold;
}
.status-pending {
    color: var(--color-status-pending);
    font-weight: bold;
}
.status-cancelled {
    color: var(--color-status-cancelled);
    font-weight: bold;
}
.status-default {
    color: #4B5563;
}

/* --- Modification Form --- */
.modification-section {
    margin-top: 20px;
    padding: 20px;
    border-radius: 12px;
    background-color: var(--color-cream-yellow); /* Cream Yellow */
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.05);
}

.section-heading-cream {
    font-size: 1.25rem;
    font-weight: 700;
    color: #5d0e1f; /* Burgundy heading on cream background */
    margin-bottom: 15px;
}

.modification-subtitle {
    font-size: 0.875rem; /* text-sm */
    color: #374151; /* Gray 700 */
    margin-bottom: 16px;
}

.modification-form {
    display: flex;
    flex-direction: column;
    gap: 16px; /* space-y-4 */
}

.form-group {
    display: block;
}

.form-label {
    display: block;
    color: #374151; /* Gray 700 */
    font-weight: 500;
    margin-bottom: 4px;
}

.form-input {
    width: 100%;
    padding: 10px;
    margin-top: 5px;
    border: 1px solid #D1D5DB;
    border-radius: 6px;
    background-color: #FFFFFF; 
    color: #1F2937; 
    transition: border-color 0.2s;
    box-sizing: border-box; 
}

.form-input:focus {
    border-color: #FFC72C;
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 199, 44, 0.3); 
}

.form-hint {
    font-size: 0.75rem; /* text-xs */
    color: #6B7280; /* Gray 500 */
    margin-top: 4px; /* mt-1 */
}

.submit-btn {
    width: 100%;
    padding: 12px;
    background-color: #5d0e1f;
    color: #FFC72C;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;
    letter-spacing: 0.5px;
    margin-top: 12px; /* Add some margin above the button */
}

.submit-btn:hover:not(:disabled) {
    background-color: #8B0000; 
    transform: translateY(-1px);
}

.submit-btn:disabled {
    background-color: #BDBDBD; 
    cursor: not-allowed;
    color: #4B5563;
}

.submit-message-base {
    margin-top: 12px; /* mt-3 */
    text-align: center;
    padding: 12px;
    border-radius: 8px;
    font-size: 0.875rem;
}

.submit-success-message {
    color: #065F46; /* text-green-800 */
    background-color: #DCFCE7; /* bg-green-100 */
}

.submit-error-message {
    color: #5d0e1f;
    background-color: var(--color-burgundy-light);
}

/* --- Loading State --- */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px; /* p-8 */
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-width: 400px;
    margin: 40px auto;
}

.loading-text {
    margin-top: 16px; /* mt-4 */
    font-size: 1.125rem; /* text-lg */
    color: var(--color-burgundy-dark);
}

/* --- Error State --- */
.error-box {
    background-color: var(--color-burgundy-light);
    color: #5d0e1f;
    padding: 30px;
    border-radius: 12px;
    border: 2px solid #5d0e1f;
    text-align: center;
    max-width: 450px;
    margin: 40px auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/** Additional things for forms */
/* Action Row for the main buttons */
.action-row {
    display: flex;
    gap: 15px;
    margin: 25px 0;
}

/* Base Button Styles */
.btn-update, .btn-cancel, .btn-confirm-save {
    flex: 1;
    padding: 14px 20px;
    border-radius: 50px; /* Elegant pill shape */
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.85rem;
}

/* Update Button: Maroon and Gold */
.btn-update {
    background-color: #5d0e1f; /* Maroon */
    color: #FFC72C; /* Gold */
    box-shadow: 0 4px 10px rgba(93, 14, 31, 0.2);
}

.btn-update:hover {
    background-color: #4a0c19;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(93, 14, 31, 0.3);
}

/* Cancel Button: Earthy/Muted Tone */
.btn-cancel {
    background-color: transparent;
    color: #A0522D; /* Earth Tone */
    border: 2px solid #A0522D;
}

.btn-cancel:hover {
    background-color: #fff1eb;
    color: #8B4513;
}

.preview-selection-area {
    background: #fffcf0; /* Light cream/gold wash */
    border: 2px dashed #FFC72C; /* Gold dashed border */
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 30px;
    animation: fadeIn 0.5s ease-out;
}

.preview-title {
    color: #5d0e1f;
    font-size: 1.1rem;
    margin-bottom: 15px;
    text-align: center;
    font-family: 'Georgia', serif;
    font-style: italic;
}

.preview-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
}

.preview-label {
    display: block;
    font-size: 0.75rem;
    color: #8B795E;
    text-transform: uppercase;
}

.preview-value {
    display: block;
    font-weight: 700;
    color: #1F2937;
    font-size: 1rem;
}

/* Confirm Save Button: Bright Gold */
.btn-confirm-save {
    background-color: #FFC72C; /* Gold */
    color: #5d0e1f; /* Maroon text */
    width: 100%;
    box-shadow: 0 4px 12px rgba(255, 199, 44, 0.3);
}

.btn-confirm-save:hover {
    background-color: #eab308;
    transform: scale(1.02);
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
/** End here */

.error-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.error-message {
    font-size: 1.1rem;
    margin-bottom: 8px;
}

.error-support-text {
    font-size: 0.875rem; /* text-sm */
    margin-top: 12px; /* mt-3 */
}

/* --- Loader --- */
.loader {
    border: 4px solid #F3F4F6;
    border-top: 4px solid #5d0e1f;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>